// Google Ads + GA4 Tracking-Wrapper mit Consent Mode v2.
// IDs kommen aus .env, damit sie ohne Code-Änderung konfigurierbar sind:
//
//   REACT_APP_GOOGLE_ADS_ID                  z. B. "AW-1234567890"   (Conversion-Konto-ID)
//   REACT_APP_GOOGLE_ADS_LEAD_LABEL          Conversion-Label „Lead/Anfrage" aus dem Ads-UI
//   REACT_APP_GOOGLE_ADS_PURCHASE_LABEL      Conversion-Label „Buchung B2C (Selbstzahler)"
//   REACT_APP_GOOGLE_ADS_B2B_PURCHASE_LABEL  Conversion-Label „Buchung B2B (Pflegedienst)" –
//                                            optional; ohne eigenes Label läuft B2B über das B2C-Label
//   REACT_APP_GA_MEASUREMENT_ID              z. B. "G-XXXXXXXXXX"    (GA4 – optional)
//
// Datenschutz: Das gtag-Script wird ERST nach Marketing-Einwilligung geladen
// (dh-consent=accepted, siehe CookieBanner). Die Consent-Mode-v2-Signale
// (ad_user_data, ad_personalization) sind seit März 2024 im EWR Pflicht,
// damit Google Ads Conversions überhaupt verarbeitet.

const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "";
const LEAD_LABEL = process.env.REACT_APP_GOOGLE_ADS_LEAD_LABEL || "";
const PURCHASE_LABEL = process.env.REACT_APP_GOOGLE_ADS_PURCHASE_LABEL || "";
const B2B_PURCHASE_LABEL = process.env.REACT_APP_GOOGLE_ADS_B2B_PURCHASE_LABEL || "";
const PHONE_LABEL = process.env.REACT_APP_GOOGLE_ADS_PHONE_LABEL || "";
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

let initialized = false;

function hasConsent() {
  try {
    return localStorage.getItem("dh-consent") === "accepted";
  } catch {
    return false;
  }
}

function injectGtagScript() {
  if (document.getElementById("gtag-script")) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };

  // Consent Mode v2 (Advanced): gtag lädt IMMER, aber standardmäßig mit
  // "denied" – dann sendet Google nur cookielose, anonyme Pings und
  // MODELLIERT Conversions von Nutzern ohne Einwilligung (DSGVO-konform,
  // offizieller Google-Standard). Nach Klick auf "Akzeptieren" wird per
  // consent-update auf "granted" hochgestuft.
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    wait_for_update: 500,
  });
  window.gtag("set", "ads_data_redaction", true);
  window.gtag("set", "url_passthrough", true);
  window.gtag("js", new Date());
  if (ADS_ID) window.gtag("config", ADS_ID);
  if (GA_ID) window.gtag("config", GA_ID, { anonymize_ip: true, send_page_view: false });

  const s = document.createElement("script");
  s.id = "gtag-script";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${ADS_ID || GA_ID}`;
  document.head.appendChild(s);
}

/** Hebt die Consent-Signale auf "granted" (nach Klick auf "Alle akzeptieren"). */
export function grantConsent() {
  if (!window.gtag) return;
  window.gtag("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  });
}

export function bootAnalytics() {
  if (initialized) return;
  if (!ADS_ID && !GA_ID) return;
  injectGtagScript();
  initialized = true;
  if (hasConsent()) grantConsent();
  // Initialen Pageview nachholen (send_page_view ist deaktiviert wegen SPA-Routing)
  trackPageView(window.location.pathname + window.location.search);
}

/** SPA-Pageview – wird vom PageViewTracker bei jedem Routenwechsel gerufen. */
export function trackPageView(path) {
  if (!initialized || !window.gtag || !GA_ID) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

/** Lead-Conversion: Beratungsanfrage, Lead-Magnet-Download etc.
 *  Default-Wert 5 € = abgestimmt auf die Google-Ads-Conversion „Atlas Lead-Magnet" (5,00 €). */
export function trackLeadConversion(value = 5, source = undefined) {
  if (!window.gtag) return;
  if (ADS_ID && LEAD_LABEL) {
    window.gtag("event", "conversion", {
      send_to: `${ADS_ID}/${LEAD_LABEL}`,
      value,
      currency: "EUR",
    });
  }
  window.gtag("event", "generate_lead", { value, currency: "EUR", lead_source: source });
}

/**
 * Kauf-Conversion: bezahlte Buchung / eBook / Buchung auf Rechnung (Bestellung).
 * segment: "b2c" (Selbstzahler) oder "b2b" (Pflegedienst / Jahrespaket).
 * B2B-Buchungen werden – sofern ein eigenes Ads-Label hinterlegt ist – als
 * separate Conversion-Aktion gemeldet, damit Smart Bidding lernt, welche
 * Klicks die wertvollen B2B-Abschlüsse bringen.
 */
export function trackPurchase(value = 0, transactionId = undefined, segment = "b2c") {
  if (!window.gtag) return;
  // Dedup: Erfolgsseiten können neu geladen werden – pro Transaktion nur 1 Conversion.
  if (transactionId) {
    try {
      const key = `dh-purchase-${transactionId}`;
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch {}
  }
  const label = segment === "b2b" && B2B_PURCHASE_LABEL ? B2B_PURCHASE_LABEL : PURCHASE_LABEL;
  if (ADS_ID && label) {
    window.gtag("event", "conversion", {
      send_to: `${ADS_ID}/${label}`,
      value,
      currency: "EUR",
      transaction_id: transactionId,
    });
  }
  window.gtag("event", "purchase", {
    value,
    currency: "EUR",
    transaction_id: transactionId,
    buyer_type: segment,
  });
}

export function trackCheckoutBegin(course) {
  if (!window.gtag) return;
  window.gtag("event", "begin_checkout", {
    currency: "EUR",
    value: course?.amount || 0,
    items: course ? [{ item_id: course.course_id, item_name: course.course_title }] : [],
  });
}

/** Produkt-/Kursseiten-Aufruf – füttert Google-Ads-Remarketing & GA4-Audiences. */
export function trackViewItem(itemId, itemName, value = 0) {
  if (!window.gtag) return;
  window.gtag("event", "view_item", {
    currency: "EUR",
    value,
    items: [{ item_id: itemId, item_name: itemName }],
  });
}

/**
 * Phone-Call-Click-Tracking (Iter 27.19).
 * Wird auf alle <a href="tel:..."> gefeuert. Beweist, dass die LG1/LG2-LP
 * organisch Anrufe generiert — und macht das in Google Ads sichtbar.
 *
 * `source` z. B. "lp-lg1-lg2", "lp-pflegedienste", "course-landing".
 * Default-Wert 15 € = typischer Wert eines qualifizierten Inbound-Anrufs.
 */
export function trackPhoneCall(source = "unknown", value = 15) {
  if (!window.gtag) return;
  if (ADS_ID && PHONE_LABEL) {
    window.gtag("event", "conversion", {
      send_to: `${ADS_ID}/${PHONE_LABEL}`,
      value,
      currency: "EUR",
    });
  }
  // GA4-Event funktioniert immer (auch ohne Ads-Label)
  window.gtag("event", "phone_call_click", {
    value,
    currency: "EUR",
    call_source: source,
  });
}
